// Един `<th>`: сортиране с клик, меню с филтър, влачене за пренареждане и
// дръжка за преоразмеряване.
//
// Влаченето НЕ е единственият път за пренареждане — панелът за колони има
// стрелки нагоре/надолу, за да работи и само с клавиатура.

import type { Header } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { ColumnMenu } from "./ColumnMenu";
import type { ColumnFilterSpec } from "./types";

type Props<TData> = {
  header: Header<TData, unknown>;
  align: "left" | "right" | "center";
  /** Броят на уникалните стойности — само в клиентски режим. */
  facets?: Map<string, number> | null;
  bounds?: [number, number] | null;
  onFilterChange: (columnId: string, value: unknown) => void;
  headerPadding: string;
};

export const HeaderCell = <TData,>({
  header,
  align,
  facets,
  bounds,
  onFilterChange,
  headerPadding,
}: Props<TData>) => {
  const { t } = useTranslation();
  const column = header.column;
  const meta = column.columnDef.meta;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: column.id });

  const sortDirection = column.getIsSorted();
  const canSort = column.getCanSort();
  const canHide = column.getCanHide() && !meta?.alwaysVisible;
  // Тригерът стои ВИДИМ в покой, затова се рисува само когато вътре има какво
  // да се направи. Без тази проверка колоната-точка (празно заглавие, без
  // сортиране, нескриваема) показваше знак, който отваря меню с един надпис.
  const hasMenu = Boolean(meta?.filter) || canSort || canHide;
  const label =
    meta?.label ||
    (typeof column.columnDef.header === "string"
      ? column.columnDef.header
      : "") ||
    column.id;

  const justify =
    align === "right"
      ? "justify-end"
      : align === "center"
        ? "justify-center"
        : "justify-start";

  return (
    <th
      ref={setNodeRef}
      scope="col"
      aria-sort={
        sortDirection === "asc"
          ? "ascending"
          : sortDirection === "desc"
            ? "descending"
            : "none"
      }
      // Позицията при влачене и ширината на колоната са известни чак по време
      // на изпълнение — единствените inline стойности тук.
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
        width: header.getSize(),
        zIndex: isDragging ? 2 : undefined,
      }}
      className={`relative select-none border-b border-shade-2 bg-panel text-left ${headerPadding} ${
        isDragging ? "opacity-70" : ""
      }`}
    >
      <div className={`flex items-center gap-1 ${justify}`}>
        <button
          type="button"
          {...attributes}
          {...listeners}
          // `false` = ЗАМЕНИ подредбата. С `true` вторият клик по друга колона
          // добавяше втори ключ, който сървърният режим мълчаливо изхвърля.
          onClick={() => canSort && column.toggleSorting(undefined, false)}
          // БЕЗ `disabled`: същият бутон е и дръжката за влачене (dnd-kit
          // слага listener-ите си върху него), а изключен бутон не получава
          // pointer събития — несортируемата колона преставаше да се
          // пренарежда. Кликът и без това е ограден с `canSort`.
          className="flex min-w-0 cursor-grab items-center gap-1 rounded font-pos-mono text-[10.5px] font-bold uppercase tracking-[0.08em] text-muted transition-colors hover:text-ink active:cursor-grabbing"
        >
          <span className="truncate">
            {flexRender(column.columnDef.header, header.getContext())}
          </span>
          {sortDirection === "asc" ? (
            <CaretUpOutlined className="text-[9px] text-brand-ink" />
          ) : null}
          {sortDirection === "desc" ? (
            <CaretDownOutlined className="text-[9px] text-brand-ink" />
          ) : null}
        </button>

        {hasMenu ? (
          <ColumnMenu
            label={label}
            filterSpec={meta?.filter as ColumnFilterSpec | undefined}
            filterValue={column.getFilterValue()}
            onFilterChange={(value) => onFilterChange(column.id, value)}
            facets={facets}
            bounds={bounds}
            sortDirection={sortDirection}
            canSort={canSort}
            onSort={(direction) =>
              direction === null
                ? column.clearSorting()
                : column.toggleSorting(direction === "desc", false)
            }
            canHide={canHide}
            onHide={() => column.toggleVisibility(false)}
          />
        ) : null}
      </div>

      {/* Разделителят между колоните стои ВИДИМ в покой (тънка линия в
          border-shade-2) — той е и знакът, че колоната се оразмерява. Зоната
          за хващане е широка колкото падинга на клетката, за да се улучва и с
          мишка, и с пръст, но не ляга върху съдържанието. Колона, на която
          оразмеряването е изключено, пак получава линията — иначе решетката
          на хедъра се разпада на едно място без причина. */}
      {column.getCanResize() ? (
        <span
          role="separator"
          aria-orientation="vertical"
          aria-label={t("dataTable.resizeColumn", { column: label })}
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          // Двоен клик връща ширината по подразбиране — иначе премерена
          // накриво колона се оправя само през „Върни подредбата".
          onDoubleClick={() => column.resetSize()}
          className="group/resize absolute inset-y-0 right-0 flex w-3 cursor-col-resize touch-none select-none justify-end"
        >
          <span
            className={`my-1.5 transition-colors ${
              column.getIsResizing()
                ? "w-[2px] bg-brand"
                : "w-px bg-shade-2 group-hover/resize:bg-shade-4"
            }`}
          />
        </span>
      ) : (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-1.5 right-0 w-px bg-shade-2"
        />
      )}
    </th>
  );
};
